import React from 'react';
import {
  Code, Database, Server, Cloud, Bot, Braces, Webhook, Cpu, Workflow, Wrench, Globe, Layers
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslations } from '../translations/translations';

const Skills = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);

  const categories = [
    {
      name: language === 'en' ? 'AI_&_LLM' : 'AI_&_LLM',
      color: 'primary',
      glowColor: 'rgba(0, 255, 255, 0.5)',
      skills: [
        { name: 'OpenAI', icon: <Bot /> },
        { name: 'Anthropic', icon: <Bot /> },
        { name: 'LangChain', icon: <Workflow /> },
        { name: 'RAG', icon: <Layers /> },
        { name: 'Google Gemini', icon: <Bot /> },
        { name: 'Microsoft Azure', icon: <Cloud /> },
        { name: 'AI Agents', icon: <Cpu /> },
        { name: 'LlamaIndex', icon: <Database /> },
        { name: 'Vector Search', icon: <Database /> },
        { name: 'Voice AI (TTS/STT)', icon: <Cpu /> },
      ],
    },
    {
      name: language === 'en' ? 'BACKEND_&_FRAMEWORKS' : 'BACKEND_&_FRAMEWORKLER',
      color: 'secondary',
      glowColor: 'rgba(255, 0, 255, 0.5)',
      skills: [
        { name: 'PHP / Laravel', icon: <Code /> },
        { name: 'Python', icon: <Braces /> },
        { name: 'Node.js', icon: <Server /> },
        { name: 'Next.js', icon: <Globe /> },
        { name: 'JavaScript', icon: <Braces /> },
        { name: 'REST API', icon: <Webhook /> },
      ],
    },
    {
      name: language === 'en' ? 'DATABASES' : 'VERİTABANLARI',
      color: 'accent',
      glowColor: 'rgba(157, 0, 255, 0.5)',
      skills: [
        { name: 'MySQL', icon: <Database /> },
        { name: 'PostgreSQL', icon: <Database /> },
      ],
    },
    {
      name: language === 'en' ? 'TOOLS_&_PLATFORMS' : 'ARAÇLAR_&_PLATFORMLAR',
      color: 'primary',
      glowColor: 'rgba(0, 255, 255, 0.5)',
      skills: [
        { name: 'n8n', icon: <Workflow /> },
        { name: 'Cloudflare', icon: <Globe /> },
        { name: 'Netlify', icon: <Globe /> },
        { name: 'Docker', icon: <Server /> },
        { name: 'GitHub', icon: <Code /> },
        { name: 'GitLab', icon: <Code /> },
        { name: 'Jira', icon: <Layers /> },
        { name: 'Microsoft Azure', icon: <Cloud /> },
        { name: 'Google API', icon: <Globe /> },
        { name: 'Meta API', icon: <Globe /> },
        { name: 'SCORM 1.2/2004', icon: <Layers /> },
        { name: language === 'en' ? 'Server Setup' : 'Sunucu Kurulumu', icon: <Server /> },
        { name: 'YouTube Content Creator', icon: <Globe /> },
        { name: language === 'en' ? 'PayTR / iyzico (Payment)' : 'PayTR / iyzico (Ödeme)', icon: <Layers /> },
      ],
    },
  ];

  return (
    <section id="skills" className="bg-background text-text py-24 relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="container mx-auto px-8 md:px-16 relative z-10">
        {/* Section header */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-1 h-12 bg-gradient-to-b from-secondary to-transparent"></div>
            <div>
              <div className="text-secondary/60 text-xs mono-text tracking-widest uppercase mb-1">
                <span className="text-accent">&gt;</span> SECTION_03
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-cyber-glow cyber-title">
                [{t.skills.title}]
              </h2>
            </div>
          </div>
          <p className="mt-6 text-base text-text/60 leading-relaxed max-w-3xl">
            {language === 'en'
              ? 'Technologies I\'ve crossed paths with throughout the projects I\'ve been involved in. I accelerate my development with Vibe Coding and AI-powered tools — always eager to learn, experiment, and integrate new technologies into my workflow.'
              : 'Şu ana kadar dahil olduğum projelerde yolumun kesiştiği teknolojiler. Vibe Coding ve AI destekli araçlarla geliştirme süreçlerimi hızlandırıyorum — sürekli öğrenmeye, denemeye ve yeni teknolojileri iş akışıma dahil etmeye istekliyim.'}
          </p>
        </div>

        {/* Skills categories */}
        <div className="space-y-16">
          {categories.map((category, catIndex) => (
            <div key={category.name} className="group/category">
              {/* Category header */}
              <div className="flex items-center gap-4 mb-8">
                <div className={`w-3 h-3 bg-${category.color} rounded-sm rotate-45`}></div>
                <h3 className={`text-2xl font-bold text-${category.color} cyber-title mono-text`}>
                  {'// ' + category.name}
                </h3>
                <div className={`flex-1 h-px bg-gradient-to-r from-${category.color}/30 to-transparent`}></div>
                <div className="cyber-card px-4 py-1">
                  <span className={`text-${category.color}/60 text-xs mono-text`}>
                    [{category.skills.length}]
                  </span>
                </div>
              </div>

              {/* Skills grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {category.skills.map((skill, skillIndex) => (
                  <div
                    key={skill.name}
                    className="cyber-card p-6 relative group/skill transition-all duration-300 hover:scale-110"
                    style={{
                      animationDelay: `${skillIndex * 0.1}s`,
                      transitionDelay: `${skillIndex * 0.05}s`
                    }}
                  >
                    {/* Hover glow effect */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover/skill:opacity-100 transition-opacity duration-300 rounded-lg"
                      style={{
                        boxShadow: `0 0 30px ${category.glowColor}, inset 0 0 20px ${category.glowColor}`
                      }}
                    ></div>

                    {/* Corner accent */}
                    <div className={`absolute top-0 right-0 w-2 h-2 bg-${category.color}/0 group-hover/skill:bg-${category.color} transition-colors duration-300`}></div>

                    {/* Content */}
                    <div className="relative flex flex-col items-center justify-center gap-4">
                      <div className={`text-4xl text-${category.color} transition-all duration-300 group-hover/skill:scale-125 group-hover/skill:rotate-12`}>
                        {skill.icon}
                      </div>
                      <div className="text-center">
                        <h4 className="text-sm font-bold text-text/90 mono-text">
                          {skill.name}
                        </h4>
                      </div>

                      {/* Bottom progress indicator */}
                      <div className="absolute bottom-2 left-2 right-2 h-1 bg-text/10">
                        <div className={`h-full bg-${category.color} transition-all duration-500 w-0 group-hover/skill:w-full`}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Separator */}
              {catIndex < categories.length - 1 && (
                <div className="mt-16 flex items-center justify-center">
                  <div className="w-px h-8 bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom decorative element */}
        <div className="mt-24 flex justify-center">
          <div className="cyber-card px-8 py-4 mono-text text-xs text-primary/60">
            <span className="text-secondary">&gt;</span> {language === 'en' ? 'SKILLS_LOADED' : 'YETENEKLER_YÜKLENDİ'} <span className="text-primary animate-pulse">_</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
